import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Container,
  Heading,
  Text,
  FormErrorMessage,
  IconButton,
  Select,
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import LogStream from './LogStream';

function CreateClient() {
  const [formData, setFormData] = useState({
    name: '',
    cvr: '',
    contactPerson: '',
    email: '',
    phone: '',
    payrollSystem: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showLogs, setShowLogs] = useState(false);
  const [newClientId, setNewClientId] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Kundenavn er påkrævet';
    if (!formData.cvr) newErrors.cvr = 'CVR nummer er påkrævet';
    if (!formData.contactPerson) newErrors.contactPerson = 'Kontaktperson er påkrævet';
    if (!formData.email) newErrors.email = 'Email er påkrævet';
    if (!formData.phone) newErrors.phone = 'Telefonnummer er påkrævet';
    if (!formData.payrollSystem) newErrors.payrollSystem = 'Lønsystem er påkrævet';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const tempPassword = 'DinLon2024!';
      const response = await adminApi.createClient({
        ...formData,
        password: tempPassword
      });

      setNewClientId(response.id);
      setShowLogs(true);
      
      toast({
        title: 'Kunde oprettet',
        description: 'Kunden er blevet oprettet succesfuldt',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
    } catch (error) {
      toast({
        title: 'Fejl ved oprettelse',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Container maxW="container.md" py={5}>
      <VStack spacing={5} align="stretch">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading size="lg">Opret ny kunde</Heading>
          <IconButton
            icon={<SettingsIcon />}
            onClick={() => setShowLogs(!showLogs)}
            variant="ghost"
            colorScheme="gray"
            aria-label="Vis logs"
          />
        </Box>
        <Text>Udfyld nedenstående formular for at oprette en ny kunde</Text>
        
        <Box as="form" onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isInvalid={errors.name}>
              <FormLabel>Kundenavn</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Indtast kundenavn"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.cvr}>
              <FormLabel>CVR Nummer</FormLabel>
              <Input
                name="cvr"
                value={formData.cvr}
                onChange={handleChange}
                placeholder="12345678"
                maxLength={8}
              />
              <FormErrorMessage>{errors.cvr}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.contactPerson}>
              <FormLabel>Kontaktperson</FormLabel>
              <Input
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                placeholder="Indtast navn på kontaktperson"
              />
              <FormErrorMessage>{errors.contactPerson}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.email}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="kunde@eksempel.dk"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.phone}>
              <FormLabel>Telefon</FormLabel>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+45 12345678"
              />
              <FormErrorMessage>{errors.phone}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.payrollSystem}>
              <FormLabel>Lønsystem</FormLabel>
              <Select
                name="payrollSystem"
                value={formData.payrollSystem}
                onChange={handleChange}
                placeholder="Vælg lønsystem"
              >
                <option value="Lessor">Lessor</option>
                <option value="Zenegy">Zenegy</option>
              </Select>
              <FormErrorMessage>{errors.payrollSystem}</FormErrorMessage>
            </FormControl>

            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isLoading}
              width="full"
            >
              Opret kunde
            </Button>
          </VStack>
        </Box>
      </VStack>

      <LogStream 
        isOpen={showLogs}
        onClose={() => {
          setShowLogs(false);
          if (newClientId) {
            navigate('/admin');
          }
        }}
        clientId={newClientId}
      />
    </Container>
  );
}

export default CreateClient; 